import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRepeat } from 'react-bootstrap-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import useIsMobile from '../../../hooks/useIsMobile';
import useAPI from '../../../hooks/useApi';
import PrecisionAIIcon from '../../shared/PrecisionAIIcon';
import { getRuleDisplayName } from '../../../constants/ruleTypeDefinitions';
import {
  buildViolationPatternStats,
  formatViolationRate,
} from '../../../utils/violationPatternStats';

function Stat({ label, value, hint, colors, textValue = false, isMobile = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: '0.625rem',
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: textValue ? (isMobile ? '0.875rem' : '0.9375rem') : '1.25rem',
          fontFamily: textValue ? 'inherit' : 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 700,
          color: colors.text,
          lineHeight: 1.25,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </div>
      {hint ? (
        <div
          style={{
            marginTop: 3,
            fontSize: '0.6875rem',
            color: colors.textMuted,
            lineHeight: 1.3,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Rates plus a short AI read of the selected violations window.
 * Sentences are cached on the server per account for the ET day.
 */
const ViolationPatternSummary = ({
  violations = [],
  timeRange = 'month',
  includePlanDeviations = false,
  accountId = null,
}) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const { request } = useAPI();
  const [sentences, setSentences] = useState([]);
  const [obsLoading, setObsLoading] = useState(false);
  const [obsError, setObsError] = useState(null);
  const requestSeq = useRef(0);

  const loadObservations = useCallback(async (refresh = false) => {
    if (!accountId) {
      setSentences([]);
      setObsError(null);
      setObsLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setObsLoading(true);
    setObsError(null);
    try {
      const params = new URLSearchParams({
        account_id: accountId,
        time_range: timeRange,
        include_plan_deviations: includePlanDeviations ? '1' : '0',
      });
      if (refresh) params.set('refresh', '1');
      const json = await request(
        `${BACKEND_URL}/trading_rules_violations/observations?${params.toString()}`,
        {},
        'GET'
      );
      if (seq !== requestSeq.current) return;
      if (!Array.isArray(json?.sentences)) {
        throw new Error(json?.error || 'Could not load observations');
      }
      setSentences(json.sentences);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setObsError(err?.message || 'Could not load observations');
    } finally {
      if (seq === requestSeq.current) setObsLoading(false);
    }
  }, [accountId, includePlanDeviations, request, timeRange]);

  useEffect(() => {
    loadObservations(false);
  }, [loadObservations]);

  const stats = useMemo(
    () => buildViolationPatternStats(violations, timeRange, { includePlanDeviations }),
    [includePlanDeviations, timeRange, violations]
  );

  const mostCommonLabel = stats.mostCommon
    ? getRuleDisplayName(stats.mostCommon.type, true)
    : '—';
  const mostCommonHint = stats.mostCommon
    ? `${stats.mostCommon.count} of ${stats.total}`
    : 'None in this range';

  return (
    <div
      data-testid="violation-pattern-summary"
      style={{
        minWidth: 0,
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: isMobile ? '12px 16px' : 16,
        }}
      >
        <Stat
          label="Avg / day"
          value={formatViolationRate(stats.avgPerDay)}
          colors={colors}
        />
        <Stat
          label="Avg / week"
          value={formatViolationRate(stats.avgPerWeek)}
          hint={stats.weekIsPace && stats.total > 0 ? 'Pace from this range' : null}
          colors={colors}
        />
        <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <Stat
            label="Most common"
            value={mostCommonLabel}
            hint={mostCommonHint}
            colors={colors}
            textValue
            isMobile={isMobile}
          />
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <PrecisionAIIcon size={26} active />
            <span
              style={{
                fontSize: '0.6875rem',
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
              }}
            >
              Observations
            </span>
          </div>
          <button
            type="button"
            aria-label="Refresh observations"
            title="Refresh observations"
            onClick={() => loadObservations(true)}
            disabled={obsLoading || !accountId}
            style={{
              background: 'none',
              border: 'none',
              padding: 2,
              margin: 0,
              color: colors.textMuted,
              opacity: obsLoading ? 0.35 : 0.75,
              cursor: obsLoading || !accountId ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ArrowRepeat size={14} />
          </button>
        </div>
        {obsLoading && sentences.length === 0 ? (
          <div style={{ marginTop: 8, fontSize: '0.75rem', color: colors.textMuted, lineHeight: 1.4 }}>
            Reading this range…
          </div>
        ) : null}
        {obsError ? (
          <div style={{ marginTop: 8, fontSize: '0.75rem', color: colors.textMuted, lineHeight: 1.4 }}>
            {obsError}
          </div>
        ) : null}
        {sentences.map((line, index) => (
          <div
            key={`${index}-${line}`}
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 8,
              minWidth: 0,
            }}
          >
            <span
              aria-hidden
              style={{
                color: colors.primary,
                fontWeight: 700,
                lineHeight: 1.35,
                flexShrink: 0,
              }}
            >
              ·
            </span>
            <span
              style={{
                fontSize: isMobile ? '0.75rem' : '0.8125rem',
                lineHeight: 1.4,
                color: colors.text,
              }}
            >
              {line}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ViolationPatternSummary;
